import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CreditActions, { CreditSelectors } from "Redux/CreditRedux";
import Spinner from "../components/Spinner";

// Action type labels for display
const ACTION_LABELS = {
  image_generation: "Image Generation",
  image_regeneration: "Image Regeneration",
  video_generation: "Video Generation",
  video_regeneration: "Video Regeneration",
  image_and_video_regeneration: "Image & Video Regeneration",
  subtitle_generation: "Subtitle Generation",
};

// Format date for display
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function CreditHistoryPage() {
  const dispatch = useDispatch();
  const history = useSelector(CreditSelectors.getHistory);
  const isFetching = useSelector(state => state.credits.historyFetching);
  const totalCount = useSelector(state => state.credits.historyTotalCount);
  
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const limit = 20;

  // Fetch history on mount and when page/filter changes
  useEffect(() => {
    const params = {
      limit,
      offset: (page - 1) * limit,
    };
    if (filter !== "all") {
      params.action_type = filter;
    }
    dispatch(CreditActions.creditsHistoryRequest(params));
  }, [dispatch, page, filter]);

  const totalPages = Math.ceil(totalCount / limit);

  const actionTypes = [
    { value: "all", label: "All Actions" },
    { value: "image_generation", label: "Image Generation" },
    { value: "image_regeneration", label: "Image Regeneration" },
    { value: "video_generation", label: "Video Generation" },
    { value: "video_regeneration", label: "Video Regeneration" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center gap-1 mb-4 min-h-[44px]"
        >
          ← Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Credit History</h1>
        <p className="text-gray-500 mt-1">
          View your credit usage history
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
        >
          {actionTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500">
          {totalCount} total transactions
        </span>
      </div>

      {/* History table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isFetching ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No credit history yet</h3>
            <p className="text-gray-500 text-sm mb-6">Your credit usage will appear here after you create your first video.</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors"
            >
              Create a Video
            </Link>
          </div>
        ) : (
          <>
          <table className="w-full hidden md:table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Action
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Credits
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{formatDate(item.created_at)}</div>
                    <div className="text-xs text-gray-500">{formatTime(item.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">
                      {ACTION_LABELS[item.action_type] || item.action_type}
                    </div>
                    {item.script_id && (
                      <div className="text-xs text-gray-500">
                        Script #{item.script_id}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-medium text-gray-900">
                      {item.credits_used}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {item.status === "success" ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ✗ Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card layout */}
          <div className="md:hidden divide-y divide-gray-100">
            {history.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-start justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-900">
                    {ACTION_LABELS[item.action_type] || item.action_type}
                  </span>
                  {item.status === "success" ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      ✗
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{formatDate(item.created_at)} {formatTime(item.created_at)}</span>
                  <span className="font-medium text-gray-900">{item.credits_used} credits</span>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
